import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { formatPrice, getAllPriceFormats } from '../utils/currency';
import ImageSeparator from '../components/ImageSeparator';

const FAQ = () => {
  const [openSection, setOpenSection] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null);

  const faqSections = [
    {
      title: "Parents",
      questions: [
        {
          question: "What is covered under AMHAS?",
          answer: `AMHAS provides 100% coverage at AMHAS Urgent Care Facilities for: urgent care & clinical treatment, laboratory services, radiology & diagnostic imaging, essential pharmaceuticals, observation beds, and non-surgical emergency care. AI + Telehealth services are also 100% covered at school. Coverage is for children ages 2-18.`
        },
        {
          question: "How much does it cost?",
          answer: `Annual premium: ${formatPrice('USD')} (${formatPrice('CFA')} / ${formatPrice('GMD')}) per child. One price for all families - no urban/rural distinction. Early sign-ups receive a 1,200 CFA discount.`
        },
        {
          question: "How does care work?",
          answer: "Care is delivered through two pathways: (1) At AMHAS Urgent Care Facilities with 100% coverage for all services, or (2) In the classroom via AI + Telehealth. At school, a staff member records vitals and symptoms; AI proposes a diagnosis; a Western board-certified doctor reviews and finalizes care. As we scale, urgent care centers are being established while AI and telehealth expand to all schools."
        },
        {
          question: "When do we use AMHAS Urgent Care Facilities?",
          answer: "Approximately 90% of cases are handled by AI overseen by an overseas medical doctor at school. When care exceeds what can be done at school (procedures, advanced diagnostics, extended monitoring), children are referred to AMHAS Urgent Care Facilities where all services are covered at 100%."
        },
        {
          question: "How do I renew my child's coverage?",
          answer: "Coverage renewal is automatic each year. You'll receive a reminder 30 days before your renewal date with payment instructions."
        },
        {
          question: "What's not covered?",
          answer: "AMHAS does not cover chronic or terminal illnesses, major surgeries requiring extended care, hospital stays longer than 3 months, pandemic-related illnesses, cosmetic procedures, or experimental treatments."
        },
        {
          question: "How does telehealth work?",
          answer: "AI + Telehealth: vitals/symptoms are entered at school using our mobile app and smart vitals device; AI analyzes and proposes a diagnosis; a Western board-certified doctor reviews and finalizes the care plan. Follow-ups happen by chat/voice/video. If care exceeds what can be done at school, referral to AMHAS Urgent Care Facility. Telehealth is not for emergencies."
        },
        {
          question: "What ages are covered?",
          answer: "AMHAS covers children ages 2-18 years old."
        }
      ]
    },
    {
      title: "Schools",
      questions: [
        {
          question: "How do we set up AMHAS at our school?",
          answer: "Contact us to discuss enrollment opportunities. We'll help you with the enrollment process and provide all necessary materials and training."
        },
        {
          question: "How much time does it require?",
          answer: "Initial setup takes about 2-3 weeks. Ongoing management requires minimal time - mainly coordinating enrollment sessions and distributing materials to parents."
        },
        {
          question: "How do we handle student data?",
          answer: "We follow strict data protection protocols. Student information is encrypted and only used for healthcare purposes. We provide training on data handling best practices."
        },
        {
          question: "What support do you provide?",
          answer: "We provide comprehensive support including training materials, enrollment tools, health education resources, and ongoing assistance from our team."
        }
      ]
    },
    {
      title: "Investors/Donors",
      questions: [
        {
          question: "How sustainable is the AMHAS model?",
          answer: "Our prevention-first approach reduces long-term costs while improving health outcomes. We maintain healthy unit economics with a surplus of 500-1,000 CFA per child annually."
        },
        {
          question: "What are the key performance indicators?",
          answer: "Key KPIs include enrollment growth, claims ratio, customer satisfaction, school attendance improvements, and family healthcare savings."
        },
        {
          question: "How often do you report on impact?",
          answer: "We provide quarterly impact reports with detailed metrics, case studies, and financial performance. Annual reports include third-party audits and verification."
        },
        {
          question: "What are the expansion plans?",
          answer: "We plan to scale from 100k to 1M+ children over 5 years. As we scale, AMHAS Urgent Healthcare Centers are being established while our AI and telehealth services expand to all schools, ensuring comprehensive care delivery through both pathways across Cameroon & The Gambia."
        }
      ]
    }
  ];

  const toggleSection = (sectionIndex) => {
    setOpenSection(openSection === sectionIndex ? null : sectionIndex);
    setOpenQuestion(null);
  };

  const toggleQuestion = (sectionIndex, questionIndex) => {
    const key = `${sectionIndex}-${questionIndex}`;
    setOpenQuestion(openQuestion === key ? null : key);
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_front_waiting_Area.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Frequently Asked Questions</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              Find answers to common questions about AMHAS
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="relative section-padding bg-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_front_desk.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            {faqSections.map((section, sectionIndex) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
                viewport={{ once: true }}
                className="mb-8"
              >
                <button
                  onClick={() => toggleSection(sectionIndex)}
                  className="w-full text-left p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                    {openSection === sectionIndex ? (
                      <ChevronUp size={24} className="text-primary-600" />
                    ) : (
                      <ChevronDown size={24} className="text-primary-600" />
                    )}
                  </div>
                </button>

                <AnimatePresence>
                  {openSection === sectionIndex && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 space-y-4"
                    >
                      {section.questions.map((item, questionIndex) => (
                        <div key={questionIndex} className="border border-gray-200 rounded-lg">
                          <button
                            onClick={() => toggleQuestion(sectionIndex, questionIndex)}
                            className="w-full text-left p-4 hover:bg-gray-50 transition-colors duration-200"
                          >
                            <div className="flex items-center justify-between">
                              <h3 className="text-lg font-semibold text-gray-900 pr-4">
                                {item.question}
                              </h3>
                              {openQuestion === `${sectionIndex}-${questionIndex}` ? (
                                <ChevronUp size={20} className="text-primary-600 flex-shrink-0" />
                              ) : (
                                <ChevronDown size={20} className="text-primary-600 flex-shrink-0" />
                              )}
                            </div>
                          </button>

                          <AnimatePresence>
                            {openQuestion === `${sectionIndex}-${questionIndex}` && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="px-4 pb-4"
                              >
                                <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Separator Banner */}
      <ImageSeparator 
        image="/urgentcare_doctor_greet_1.png"
        title="Still Have Questions?"
        subtitle="Get in touch with our team for personalized assistance"
      />

      {/* Contact Section */}
      <section className="relative section-padding bg-gray-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_front_area_1.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Can't find the answer you're looking for? Our team is here to help.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a href="/contact-form" className="btn-primary">
                Email Us
              </a>
              <a href="tel:+2204040966" className="btn-secondary">
                Call Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
